import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, LayoutDashboard, CalendarClock, Bell, BookOpen, Shield, LogOut, ChevronRight, MailWarning } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Loading } from "@/components/States";
import { ZipInput } from "@/components/ZipInput";
import { TrustSummary } from "@/components/TrustSummary";
import { inputCls, btnPrimary, card, label } from "@/lib/ui";

const Row = ({ to, icon: Icon, children, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors">
    <Icon className="h-5 w-5 text-slate-500" /> <span className="flex-1 text-sm font-semibold text-slate-800">{children}</span> <ChevronRight className="h-4 w-4 text-slate-400" />
  </Link>
);

const PasswordForm = () => {
  const { signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async () => {
    try {
      const { data } = await api.post("/auth/change-password", f);
      signIn(data);
      toast.success("Password changed. Other sessions were signed out.");
      setF({ current_password: "", new_password: "" });
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!open) return <button data-testid="change-password-open" onClick={() => setOpen(true)} className="text-sm font-semibold text-blue-600">Change password</button>;
  return (
    <div data-testid="change-password-form" className={card + " space-y-3"}>
      <div><span className={label}>Current password</span><input data-testid="current-password-input" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={inputCls} /></div>
      <div><span className={label}>New password (10+ characters)</span><input data-testid="new-password-input" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={inputCls} /></div>
      <button data-testid="change-password-submit" disabled={!f.current_password || f.new_password.length < 10} onClick={submit} className={btnPrimary}>Update password</button>
    </div>
  );
};

export default function Profile() {
  const { user, setUser, signOut } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ first_name: user.first_name, zip: user.zip });
  const [emailCfg, setEmailCfg] = useState(null);
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get(`/users/${user.id}/profile`).then(({ data }) => setProfile(data)).catch(() => {});
  }, [user.id]);
  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setEmailCfg(data.email_reminders)).catch(() => {});
  }, [load]);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/me", patch);
      setUser(data);
      toast.success("Profile updated");
      setEdit(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const onPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      await save({ photo: data.path });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-16">
      <div className={card}>
        <div className="flex items-center gap-4">
          <button data-testid="profile-photo-btn" onClick={() => fileRef.current.click()} className="relative" aria-label="Change photo">
            <Avatar user={user} size={72} />
            <span className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center border-2 border-white"><Camera className="h-3.5 w-3.5" /></span>
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => onPhoto(e.target.files[0])} data-testid="profile-photo-input" />
          <div className="flex-1 min-w-0">
            <h1 data-testid="profile-name" className="text-2xl font-extrabold text-slate-900">{user.first_name}</h1>
            <p className="text-sm text-slate-500">{user.city} · Joined {fmtDate(user.created_at)}</p>
            <Link to={`/u/${user.id}`} data-testid="view-public-profile" className="text-sm font-semibold text-blue-600">View public profile</Link>
          </div>
          <button data-testid="profile-edit-btn" onClick={() => setEdit(!edit)} className="text-sm font-semibold text-slate-700 hover:text-blue-700">{edit ? "Close" : "Edit"}</button>
        </div>
        {edit && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div><span className={label}>First name</span><input data-testid="profile-firstname-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} /></div>
            <div><span className={label}>Area</span><ZipInput testid="profile-zip-input" value={form.zip} onChange={(zip) => setForm({ ...form, zip })} /></div>
            <button data-testid="profile-save-btn" onClick={() => save(form)} className={btnPrimary + " sm:col-span-2"}>Save</button>
          </div>
        )}
        {user.booking_restricted_until && user.booking_restricted_until > new Date().toISOString() && (
          <p data-testid="restriction-notice" className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">New bookings are limited until {fmtDate(user.booking_restricted_until)}. <Link to="/rules" className="underline">See rules</Link></p>
        )}
      </div>
      <div className={card}>{profile ? <TrustSummary profile={profile} /> : <Loading />}</div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
        <Row to="/dashboard" icon={LayoutDashboard} testId="profile-link-dashboard">Seller dashboard</Row>
        <Row to="/pickups" icon={CalendarClock} testId="profile-link-pickups">Pickups & disputes</Row>
        <Row to="/notifications" icon={Bell} testId="profile-link-notifications">Notifications</Row>
        <Row to="/rules" icon={BookOpen} testId="profile-link-rules">Community & reliability rules</Row>
        {user.role === "admin" && <Row to="/admin" icon={Shield} testId="profile-link-admin">Admin dashboard</Row>}
      </div>
      {emailCfg && (
        <div data-testid="email-reminders-status" className={card + " flex items-start gap-3"}>
          <MailWarning className="h-5 w-5 shrink-0 text-slate-500" />
          <div className="flex-1 text-sm">
            <p className="text-slate-700">{emailCfg.message}</p>
            {emailCfg.configured && (
              <label className="mt-2 flex items-center gap-2 font-semibold">
                <input data-testid="email-reminders-toggle" type="checkbox" checked={user.email_reminders !== false} onChange={(e) => save({ email_reminders: e.target.checked })} /> Email me pickup confirmations and reminders
              </label>
            )}
          </div>
        </div>
      )}
      <PasswordForm />
      <button data-testid="logout-btn" onClick={() => { signOut(); nav("/"); }} className="w-full h-11 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-red-600 flex items-center justify-center gap-2 hover:bg-red-50"><LogOut className="h-4 w-4" /> Sign out</button>
    </div>
  );
}
